import { useState } from 'react';
import { Page, Card, DataTable, Modal, BlockStack, Text, InlineStack, Button, Frame, Toast, EmptyState } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useShop } from '../hooks/useShop';
import { useCurrency } from '../hooks/useCurrency';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';
import ExportButton from '../components/ExportButton';

interface OrderRow {
  shopify_order_id: number;
  order_name: string | null;
  order_date: string;
  total_revenue: number;
  total_cogs: number;
  net_profit: number;
  net_margin: number | null;
}

interface OrdersResponse {
  data: OrderRow[];
}

interface OrderDetail extends OrderRow {
  cost_breakdown: Record<string, number>;
  line_items: { title: string; quantity: number; revenue: number; cogs: number; profit: number }[];
}

export default function Orders() {
  const { shop } = useShop();
  const currency = shop?.currency ?? 'USD';
  const { formatMoney, formatPercent } = useCurrency(currency);
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const orders = useApi<OrdersResponse>(Endpoints.orders.list);

  // Order sync is a queued job (returns 202) — give feedback, then refresh the list a few
  // times so freshly-processed orders appear without a manual reload.
  const syncOrders = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await client.post(Endpoints.orders.sync, { days: 90 });
      setToast('Order sync started — this runs in the background and may take a moment.');
      [4000, 9000, 15000].forEach((ms) => setTimeout(() => orders.refetch(), ms));
    } catch (e) {
      setToast((e as Error).message || 'Could not start the sync. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (id: number) => {
    const res = await client.get<{ order: OrderDetail }>(Endpoints.orders.detail(id));
    setDetail(res.data.order);
  };

  const rows =
    orders.data?.data?.map((o) => [
      <Button variant="plain" onClick={() => openDetail(o.shopify_order_id)}>
        {o.order_name ?? String(o.shopify_order_id)}
      </Button>,
      new Date(o.order_date).toLocaleDateString(),
      formatMoney(o.total_revenue),
      formatMoney(o.total_cogs),
      formatMoney(o.net_profit),
      o.net_margin != null ? formatPercent(o.net_margin) : '—',
    ]) ?? [];

  return (
    <Frame>
    <Page
      title="Orders"
      primaryAction={{
        content: 'Sync orders',
        onAction: syncOrders,
        loading: syncing,
      }}
      secondaryActions={
        <ExportButton url={Endpoints.orders.export} filename="profitvault-orders.csv" />
      }
    >
      <Card>
        {/* An empty table used to be indistinguishable from "you made no profit". Orders only
            arrive after a sync, so say which one this is. */}
        {!orders.loading && rows.length === 0 ? (
          <EmptyState
            heading="No orders loaded yet"
            action={{ content: 'Sync orders', onAction: syncOrders, loading: syncing }}
            image=""
          >
            <p>
              ProfitVault reads your orders from Shopify to work out profit. Syncing pulls the
              last 60 days (as far back as Shopify allows); it runs in the background and the table fills in as it goes.
            </p>
          </EmptyState>
        ) : (
          <DataTable
            columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric']}
            headings={['Order', 'Date', 'Revenue', 'COGS', 'Net profit', 'Net margin']}
            rows={rows}
          />
        )}
      </Card>

      {detail && (
        <Modal
          open
          onClose={() => setDetail(null)}
          title={`Profit breakdown — ${detail.order_name ?? detail.shopify_order_id}`}
          size="large"
        >
          <Modal.Section>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text as="span">Revenue</Text>
                <Text as="span">{formatMoney(detail.total_revenue)}</Text>
              </InlineStack>
              {Object.entries(detail.cost_breakdown ?? {}).map(([key, value]) => (
                <InlineStack key={key} align="space-between">
                  <Text as="span" tone="subdued">
                    − {key.replace(/_/g, ' ')}
                  </Text>
                  <Text as="span" tone="subdued">
                    {formatMoney(value)}
                  </Text>
                </InlineStack>
              ))}
              <InlineStack align="space-between">
                <Text as="span" fontWeight="bold">
                  Net profit
                </Text>
                <Text as="span" fontWeight="bold">
                  {formatMoney(detail.net_profit)}
                </Text>
              </InlineStack>
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </Page>
    </Frame>
  );
}
